import { BaseUITools } from "@/uiControls/base/baseUITools";
import { createNumericSliderUI, NumericSliderUI } from "@/uiControls/base/ui/numericSliderUI";
import { globalConfig, globalManager, globalTools } from "@/utils/globalConfig";
import { VisionaryScene } from "@/visionaryControllers/visionaryScene";
import { sceneUtils } from "@/utils/sceneUtils";
import { computeQueryWeights, computeGramMatrix } from "@/utils/heatmap.func";
import { getI18nText } from "@/utils/commonFunc";
import { modelDialogTools } from "@/utils/globalConfig";

// 经语义处理的ply模型文件信息
export interface PlyModel {
    modelId: string; // 模型id，对应visionaryScene中visionaryObject的id
    modelName: string; // 模型名称
    sceneId: string; // 场景id，对应visionaryScene的id
    codebook: Float32Array | null; // 64x512 row-major
    codebookName: string | null; // 代码本名称
    loadStatus: number; // 0:未加载，1:加载中，2:加载完成，3:加载失败
    weightGram64x64: Float32Array | null; // 64x64 row-major
    weightQuery64: Float32Array | null; // 64x1 row-major
}

export class HeatmapPanelTools extends BaseUITools {
    private plyModelMap: Map<string, PlyModel> = new Map();

    private isActive: boolean = false;
    private blendBaseCheckbox: HTMLInputElement | null = null;
    private negRelevancyCheckbox: HTMLInputElement | null = null;
    private opacitySlider: NumericSliderUI | null = null;
    private thresholdSlider: NumericSliderUI | null = null;
    private tauSlider: NumericSliderUI | null = null;
    private brightnessSlider: NumericSliderUI | null = null;

    private queryDropdownButtonElement: HTMLButtonElement | null = null;
    private queryDropdownListElement: HTMLUListElement | null = null;
    private colormapDropdownButtonElement: HTMLButtonElement | null = null;
    private colormapDropdownListElement: HTMLUListElement | null = null;

    private heatmapModelsEmptyElement: HTMLElement | null = null;
    private plyModelListElement: HTMLElement | null = null;
    private queryListElement: HTMLElement | null = null;
    private heatmapBlendWithBase: boolean = true;
    private heatmapUseNegRelevancy: boolean = true;
    private heatmapRelevancyTau: number = 10.0;
    private heatmapGain: number = 1.5;
    private heatmapColormap: number = 0; // 0:Turbo,1:Viridis,2:Inferno,3:Magma,4:Plasma,5:Jet
    private heatmapQueryName: string | null = null;
    private heatmapOpacity: number = 0.6;
    private heatmapThreshold: number = 0.0;

    private languageQueries: Array<{ name: string; vector: number[] }> | null = null;
    private queryListFileName: string = '-';
    private queryListFileContentElement: HTMLElement | null = null;
    private queryListButtons: { deleteButton: HTMLElement | null, uploadButton: HTMLElement | null, fileInput: HTMLInputElement | null } = { deleteButton: null, uploadButton: null, fileInput: null };
    private readonly defaultValues = {
        codebook: '-',
        queryListFileName: '-',
        query: '',
        blendBase: true,
        negRelevancy: true,
        opacity: 0.6,
        threshold: 0.0,
        tau: 0.2,
        brightness: 0.5,
        colormap: 0,
    };
    private readonly colormapOptions: Array<{ value: number; label: string }> = [
        { value: 0, label: 'Turbo' },
        { value: 1, label: 'Viridis' },
        { value: 2, label: 'Inferno' },
        { value: 3, label: 'Magma' },
        { value: 4, label: 'Plasma' },
        { value: 5, label: 'Jet' },
    ];

    constructor(id: string) {
        super(id, false);
        this.init();
    }

    init() {
        const panelContainerElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-container',
        });

        const titleElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title',
        });
        const titleLeftElement = this.ui.createElement({
            tag: 'div',
            textContent: 'Heatmap',
            className: 'panel-setting-title-left',
        });
        const titleRightElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title-right',
        });

        const resetButtonElement = this.ui.createButton({
            textContent: '重置',
            className: ['btn-item', 'btn-text-item', 'inherit-font-size'],
            i18nKey: 'buttons.reset'
        });
        resetButtonElement.addEventListener('click', () => {
            this.onReset();
        });
        titleRightElement.appendChild(resetButtonElement);

        const closeButtonElement = this.ui.createButton({
            iconName: 'cuowu1',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
        });
        closeButtonElement.addEventListener('click', () => {
            this.onClose();
        });
        titleRightElement.appendChild(closeButtonElement);

        titleElement.appendChild(titleLeftElement);
        titleElement.appendChild(titleRightElement);
        panelContainerElement.appendChild(titleElement);

        panelContainerElement.appendChild(this.createModelFileSection());
        panelContainerElement.appendChild(this.createQueryListSection());
        panelContainerElement.appendChild(this.createHeatmapParamsSection());

        this.appendChild('panelContainer', panelContainerElement);
        this.setupEventHandlers();
    }

    private createSectionContainer(title: string, i18nKey?: string): HTMLElement {
        const sectionElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-view',
        });
        const sectionTitle = this.ui.createElement({
            tag: 'div',
            textContent: title,
            className: 'panel-setting-view-title',
            ...(i18nKey && { i18nKey }),
        });
        sectionElement.appendChild(sectionTitle);
        return sectionElement;
    }
    // 模型文件
    private createModelFileSection(): HTMLElement {
        const sectionElement = this.createSectionContainer('模型文件', 'heatmap.modelFiles');
        const codebookLabel = this.ui.createElement({
            tag: 'div',
            textContent: getI18nText('heatmap.codebook'),
            className: 'heatmap-sub-title',
            i18nKey: 'heatmap.codebook',
        });
        sectionElement.appendChild(codebookLabel);
        const heatmapModelsEmptyElement = this.ui.createElement({
            tag: 'div',
            textContent: getI18nText('heatmap.noModelFiles'),
            className: 'heatmap-models-empty',
            i18nKey: 'heatmap.noModelFiles',
        });
        sectionElement.appendChild(heatmapModelsEmptyElement);
        this.heatmapModelsEmptyElement = heatmapModelsEmptyElement;
        const modelFileListElement = this.ui.createElement({
            tag: 'div',
            className: 'heatmap-model-file-list',
        });
        sectionElement.appendChild(modelFileListElement);
        this.plyModelListElement = modelFileListElement;
        const queryLabel = this.ui.createElement({
            tag: 'div',
            textContent: getI18nText('heatmap.queryList'),
            className: 'heatmap-sub-title',
            i18nKey: 'heatmap.queryList',
        });
        sectionElement.appendChild(queryLabel);
        const queryListContainerElement = this.ui.createElement({
            tag: 'div',
            className: 'heatmap-query-list-container',
        });
        sectionElement.appendChild(queryListContainerElement);
        this.queryListElement = queryListContainerElement;
        return sectionElement;
    }

    private async loadHeatmapQueryListFile(file: File): Promise<void> {
        const text = await file.text();
        this.loadHeatmapQueryListFromText(text);
    }

    private async loadHeatmapCodebookFile(file: File, sceneId: string): Promise<void> {
        const fileName = file.name.toLowerCase();
        let nextCodebook: Float32Array;
        const plyModel = this.plyModelMap.get(sceneId);
        if (!plyModel) {
            return;
        }

        plyModel.loadStatus = 1;
        if (fileName.endsWith('.bin')) {
            const buffer = await file.arrayBuffer();
            nextCodebook = new Float32Array(buffer);
        } else {
            const text = await file.text();
            const values = text
                .split(/[\s,]+/)
                .map((item) => item.trim())
                .filter((item) => item.length > 0)
                .map((item) => Number(item));
            if (values.some((item) => !Number.isFinite(item))) {
                throw new Error('Codebook contains non-numeric values');
            }
            nextCodebook = new Float32Array(values);
        }

        if (nextCodebook.length === 0 || nextCodebook.length % 512 !== 0) {
            plyModel.loadStatus = 3;
            throw new Error('Codebook length is invalid');
        }
        plyModel.codebook = nextCodebook;
        plyModel.codebookName = file.name;
        plyModel.loadStatus = 2;
        plyModel.weightGram64x64 = computeGramMatrix(plyModel.codebook);
        plyModel.weightQuery64 = null;
        this.updateHeatmapQuery64(true);
        this.updateHeatmapPipeline();
    }

    private clearHeatmapCodebook(sceneId: string): void {
        const plyModel = this.plyModelMap.get(sceneId);
        if (!plyModel) {
            return;
        }
        plyModel.codebook = null;
        plyModel.codebookName = '';
        plyModel.loadStatus = 0;
        plyModel.weightGram64x64 = null;
        plyModel.weightQuery64 = null;
        this.updateHeatmapPipeline();
        this.updateHeatmapPanelModels();
    }

    private loadHeatmapQueryListFromText(text: string): void {
        const json = JSON.parse(text);
        const queries = json?.queries;
        if (!Array.isArray(queries) || queries.length === 0) {
            throw new Error('Query list JSON has no queries');
        }
        const normalizedQueries: Array<{ name: string; vector: number[] }> = [];
        for (const item of queries) {
            const name = item?.name;
            const vector = item?.vector;

            if (typeof name !== 'string' || !Array.isArray(vector) || vector.length !== 512) {
                throw new Error('Query list item is invalid');
            }
            normalizedQueries.push({ name, vector });
        }
        this.languageQueries = normalizedQueries;
        if (!this.heatmapQueryName || !normalizedQueries.some((item) => item.name === this.heatmapQueryName)) {
            this.heatmapQueryName = normalizedQueries[0]?.name ?? null;
        }
    }

    private updateQueryListFileElement(): void {
        if (this.queryListFileContentElement) {
            this.queryListFileContentElement.textContent = this.queryListFileName;
        }
        if (this.languageQueries && this.languageQueries.length > 0) {
            (this.queryListButtons.deleteButton as HTMLElement).style.display = 'block';
            (this.queryListButtons.uploadButton as HTMLElement).style.display = 'none';
        } else {
            (this.queryListButtons.deleteButton as HTMLElement).style.display = 'none';
            (this.queryListButtons.uploadButton as HTMLElement).style.display = 'block';
        }
    }
    private createQueryListButtons() {
        const deleteButton = this.ui.createElement({
            tag: 'button',
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'heatmap-query-trigger'],
            attributes: {
                type: 'button',
                'aria-label': 'delete-codebook',
            },
        }) as HTMLButtonElement;
        const deleteIcon = this.ui.createElement({
            tag: 'i',
            className: ['icon', 'iconfont', 'icon-shanchu'],
        });
        deleteButton.appendChild(deleteIcon);
        deleteButton.addEventListener('click', () => {
            this.queryListFileName = this.defaultValues.queryListFileName;
            this.languageQueries = null;
            this.heatmapQueryName = null;
            this.setQueryOptions([]);
            globalConfig.heatmapParams.weightQuery64 = null;
            this.updateHeatmapQuery64(true);
            this.updateHeatmapPipeline();
            this.syncFromSceneState('8888888888');
            this.updateQueryListFileElement();
        });
        this.queryListButtons.deleteButton = deleteButton;
        const queryListFileInput = this.ui.createElement({
            tag: 'input',
            attributes: {
                type: 'file',
                accept: '.json,.txt',
            },
        }) as HTMLInputElement;
        queryListFileInput.style.display = 'none';
        queryListFileInput.addEventListener('change', async () => {
            const file = queryListFileInput.files?.[0];
            if (!file) return;
            try {
                modelDialogTools.showLoadingInfoModal(getI18nText('heatmap.queryListLoading'));
                await this.loadHeatmapQueryListFile(file);
                this.queryListFileName = file.name || '-';

                globalConfig.heatmapParams.weightQuery64 = null;
                this.setQueryOptions((this.languageQueries || []).map((item) => item.name)); // refreshHeatmapQueryDropdown
                this.updateHeatmapQuery64(true);
                this.updateHeatmapPipeline();
                this.syncFromSceneState('11111111111111111');
                this.updateQueryListFileElement();
                modelDialogTools.showLoadingInfoModal(getI18nText('heatmap.queryListLoaded'));
            } catch (error) {
                console.warn('[HeatmapPanel] file load failed:', error);
                modelDialogTools.showMsgDialog(getI18nText('heatmap.queryListLoadFailed'));
            } finally {
                queryListFileInput.value = '';
                modelDialogTools.closeLoadingInfoModal(0);
            }
        });
        const queryListTriggerButton = this.ui.createElement({
            tag: 'button',
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'heatmap-query-trigger'],
            attributes: {
                type: 'button',
                'aria-label': 'select-query-list-file',
            },
        }) as HTMLButtonElement;
        const queryListTriggerIcon = this.ui.createElement({
            tag: 'i',
            className: ['icon', 'iconfont', 'icon-shangchuan'],
        });
        queryListTriggerButton.appendChild(queryListTriggerIcon);
        queryListTriggerButton.addEventListener('click', (event) => {
            event.stopPropagation();
            queryListFileInput.click();
        });
        this.queryListButtons.uploadButton = queryListTriggerButton;
        this.queryListButtons.fileInput = queryListFileInput;
    }
    private createQueryListSection(): HTMLElement {
        if (this.queryListElement) {
            const querylistUploadContainer = this.ui.createElement({
                tag: 'div',
                className: 'heatmap-file-row',
            });

            const queryListFileContent = this.ui.createElement({
                tag: 'div',
                textContent: this.queryListFileName || this.defaultValues.queryListFileName,
                className: 'heatmap-file-value',
            });
            this.queryListFileContentElement = queryListFileContent;
            querylistUploadContainer.appendChild(queryListFileContent);
            this.createQueryListButtons();
            querylistUploadContainer.appendChild(this.queryListButtons.deleteButton as HTMLElement);
            querylistUploadContainer.appendChild(this.queryListButtons.uploadButton as HTMLElement);
            querylistUploadContainer.appendChild(this.queryListButtons.fileInput as HTMLInputElement);

            this.queryListElement.appendChild(querylistUploadContainer);
        }
        const sectionElement = this.createSectionContainer('识别物体', 'heatmap.recognizedObjects');
        const queryLabel = this.ui.createElement({
            tag: 'div',
            textContent: getI18nText('heatmap.query'),
            className: 'heatmap-sub-title',
            i18nKey: 'heatmap.query',
        });
        sectionElement.appendChild(queryLabel);

        const queryItem = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-item heatmap-query-item',
        });
        const queryContent = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-item-content heatmap-query-content',
        });
        const queryDropdown = this.ui.createElement({
            tag: 'div',
            className: 'dropdown w100 heatmap-query-dropdown',
        });
        const queryButton = this.ui.createElement({
            tag: 'button',
            className: 'btn btn-secondary dropdown-toggle vision-dropdown-toggle w100 heatmap-query-toggle',
            attributes: {
                type: 'button',
                id: 'heatmapQueryButton',
                'data-bs-toggle': 'dropdown',
                'aria-expanded': 'false',
            },
        }) as HTMLButtonElement;
        const queryToggleLabel = this.ui.createElement({
            tag: 'span',
            className: 'heatmap-query-toggle-label',
            textContent: '-',
        });
        const queryToggleArrow = this.ui.createElement({
            tag: 'i',
            className: ['icon', 'iconfont', 'icon-SmallArrowOutlined', 'heatmap-query-toggle-arrow'],
            attributes: { 'aria-hidden': 'true' },
        });
        queryButton.appendChild(queryToggleLabel);
        queryButton.appendChild(queryToggleArrow);
        this.queryDropdownButtonElement = queryButton;
        const queryList = this.ui.createElement({
            tag: 'ul',
            className: 'dropdown-menu vision-dropdown-menu w100',
            id: 'heatmapQueryListDom',
            attributes: {
                'aria-labelledby': 'heatmapQueryButton',
            },
        }) as HTMLUListElement;
        this.queryDropdownListElement = queryList;
        queryDropdown.appendChild(queryButton);
        queryDropdown.appendChild(queryList);

        this.setQueryOptions([]);
        queryContent.appendChild(queryDropdown);

        queryItem.appendChild(queryContent);
        sectionElement.appendChild(queryItem);
        return sectionElement;
    }

    private createHeatmapParamsSection(): HTMLElement {
        const sectionElement = this.createSectionContainer('Heatmap 参数', 'heatmap.parameters.title');
        sectionElement.appendChild(this.createCheckboxRow('Blend Base', this.defaultValues.blendBase, (input) => {
            this.blendBaseCheckbox = input;
        }, 'heatmap.parameters.blendBase'));
        sectionElement.appendChild(this.createCheckboxRow('Neg Relevancy (object/things/stuff/texture)', this.defaultValues.negRelevancy, (input) => {
            this.negRelevancyCheckbox = input;
        }, 'heatmap.parameters.negRelevancy'));

        this.opacitySlider = this.createSlider({
            id: 'heatmap-opacity-control',
            label: 'Opacity',
            value: this.defaultValues.opacity,
            min: 0,
            max: 1,
            step: 0.01,
            precision: 2,
            i18nKey: 'heatmap.parameters.opacity',
        });
        this.thresholdSlider = this.createSlider({
            id: 'heatmap-threshold-control',
            label: 'Threshold',
            value: this.defaultValues.threshold,
            min: 0,
            max: 1,
            step: 0.01,
            precision: 2,
            i18nKey: 'heatmap.parameters.threshold',
        });
        this.tauSlider = this.createSlider({
            id: 'heatmap-tau-control',
            label: 'tau',
            value: this.defaultValues.tau,
            min: 0,
            max: 1,
            step: 0.01,
            precision: 2,
            i18nKey: 'heatmap.parameters.tau',
        });
        this.brightnessSlider = this.createSlider({
            id: 'heatmap-brightness-control',
            label: 'Brightness',
            value: this.defaultValues.brightness,
            min: 0,
            max: 1,
            step: 0.01,
            precision: 2,
            i18nKey: 'heatmap.parameters.brightness',
        });

        sectionElement.appendChild(this.opacitySlider.getContainer());
        sectionElement.appendChild(this.thresholdSlider.getContainer());
        sectionElement.appendChild(this.tauSlider.getContainer());
        sectionElement.appendChild(this.brightnessSlider.getContainer());
        sectionElement.appendChild(this.createColormapRow());

        this.bindParamEvents();
        return sectionElement;
    }

    private createColormapRow(): HTMLElement {
        const wrapper = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-view',
        });
        const label = this.ui.createElement({
            tag: 'div',
            className: 'heatmap-sub-title',
            textContent: 'Colormap',
            i18nKey: 'heatmap.parameters.colormap',
        });
        wrapper.appendChild(label);
        const content = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-item heatmap-query-item',
        });
        const dropdown = this.ui.createElement({
            tag: 'div',
            className: 'dropdown w100 heatmap-colormap-dropdown',
        });
        const dropdownButton = this.ui.createElement({
            tag: 'button',
            className: 'btn btn-secondary dropdown-toggle vision-dropdown-toggle w100 heatmap-query-toggle',
            attributes: {
                type: 'button',
                id: 'heatmapColormapButton',
                'data-bs-toggle': 'dropdown',
                'aria-expanded': 'false',
            },
        }) as HTMLButtonElement;
        this.colormapDropdownButtonElement = dropdownButton;
        const colormapToggleLabel = this.ui.createElement({
            tag: 'span',
            className: 'heatmap-query-toggle-label',
            textContent: '-',
        });
        dropdownButton.appendChild(colormapToggleLabel);
        const colormapIcon = this.ui.createElement({
            tag: 'i',
            className: ['icon', 'iconfont', 'icon-SmallArrowOutlined', 'heatmap-query-toggle-arrow'],
            attributes: { 'aria-hidden': 'true' },
        });
        dropdownButton.appendChild(colormapIcon);
        const dropdownList = this.ui.createElement({
            tag: 'ul',
            className: 'dropdown-menu vision-dropdown-menu w100',
            attributes: {
                'aria-labelledby': 'heatmapColormapButton',
            },
        }) as HTMLUListElement;
        this.colormapDropdownListElement = dropdownList;
        dropdown.appendChild(dropdownButton);
        dropdown.appendChild(dropdownList);

        this.setColormapOptions();
        content.appendChild(dropdown);
        wrapper.appendChild(content);
        return wrapper;
    }

    // codebook文件UI
    private createFileRow(
        plyModel: PlyModel,
        accept: string,
        onFileChange: (file: File, valueElement: HTMLElement) => Promise<void>,
        onDelete: () => void
    ): HTMLElement {
        const wrapper = this.ui.createElement({
            tag: 'div',
            className: 'heatmap-file-block',
        });
        const row = this.ui.createElement({
            tag: 'div',
            className: 'heatmap-file-row',
        });
        const valueElement = this.ui.createElement({
            tag: 'div',
            textContent: plyModel.codebookName || this.defaultValues.codebook,
            className: 'heatmap-file-value',
        });
        row.appendChild(valueElement);

        if (plyModel.codebook) { // 如果已加载codebook，valueElement显示codebook名称且显示删除按钮
            const deleteButton = this.ui.createElement({
                tag: 'button',
                className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'heatmap-query-trigger'],
                attributes: {
                    type: 'button',
                    'aria-label': 'delete-codebook',
                },
            }) as HTMLButtonElement;
            const deleteIcon = this.ui.createElement({
                tag: 'i',
                className: ['icon', 'iconfont', 'icon-shanchu'],
            });
            deleteButton.appendChild(deleteIcon);
            deleteButton.addEventListener('click', () => {
                onDelete();
            });
            valueElement.setAttribute('title', plyModel.codebookName || this.defaultValues.codebook);
            row.appendChild(deleteButton);
        } else { // 如果未加载codebook，valueElement显示默认提示且显示加载文件按钮
            const inputElement = this.ui.createElement({
                tag: 'input',
                attributes: {
                    type: 'file',
                    accept,
                },
            }) as HTMLInputElement;
            inputElement.style.display = 'none';

            inputElement.addEventListener('change', async () => {
                const file = inputElement.files?.[0];
                if (!file) return;
                const previousText = valueElement.textContent || '-';
                valueElement.textContent = 'Loading...';
                try {
                    await onFileChange(file, valueElement);
                    valueElement.textContent = file.name;
                    valueElement.setAttribute('title', file.name);
                } catch (error) {
                    console.warn('[HeatmapPanel] file load failed:', error);
                    valueElement.textContent = previousText;
                } finally {
                    inputElement.value = '';
                }
            });

            const fileTriggerButton = this.ui.createElement({
                tag: 'button',
                className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'heatmap-query-trigger'],
                attributes: {
                    type: 'button',
                    'aria-label': 'select-query-list-file',
                },
            }) as HTMLButtonElement;
            const fileTriggerIcon = this.ui.createElement({
                tag: 'i',
                className: ['icon', 'iconfont', 'icon-shangchuan'],
            });
            fileTriggerButton.appendChild(fileTriggerIcon);
            fileTriggerButton.addEventListener('click', (event) => {
                event.stopPropagation();
                inputElement.click();
            });
            row.appendChild(fileTriggerButton);
            wrapper.appendChild(inputElement);
        }
        wrapper.appendChild(row);
        return wrapper;
    }

    private createCheckboxRow(
        label: string,
        checked: boolean,
        bindInput: (input: HTMLInputElement) => void,
        i18nKey?: string
    ): HTMLElement {
        const row = this.ui.createElement({
            tag: 'div',
            className: 'heatmap-checkbox-row',
        });
        const input = this.ui.createElement({
            tag: 'input',
            className: 'heatmap-checkbox-input',
            attributes: {
                type: 'checkbox',
                ...(checked && { checked: 'true' }),
            },
        }) as HTMLInputElement;
        bindInput(input);
        const labelElement = this.ui.createElement({
            tag: 'label',
            textContent: label,
            className: 'heatmap-checkbox-label',
            ...(i18nKey && { i18nKey }),
        });
        labelElement.addEventListener('click', () => {
            input.click();
        });

        row.appendChild(input);
        row.appendChild(labelElement);
        return row;
    }

    private createSlider(options: {
        id: string;
        label: string;
        value: number;
        min: number;
        max: number;
        step: number;
        precision: number;
        i18nKey?: string;
    }): NumericSliderUI {
        return createNumericSliderUI({
            id: options.id,
            containerClass: 'panel-setting-item',
            label: options.label,
            showValue: false,
            value: options.value,
            min: options.min,
            max: options.max,
            step: options.step,
            precision: options.precision,
            labelClass: 'panel-setting-item-label',
            sliderContainerClass: 'panel-setting-item-content',
            i18nKey: options.i18nKey || '',
        });
    }

    onReset() {
        const {
            blendBase,
            negRelevancy,
            opacity,
            threshold,
            tau,
            brightness,
            colormap,
        } = this.defaultValues;

        if (this.blendBaseCheckbox) {
            this.blendBaseCheckbox.checked = blendBase;
        }
        if (this.negRelevancyCheckbox) {
            this.negRelevancyCheckbox.checked = negRelevancy;
        }
        this.opacitySlider?.setValue(opacity);
        this.thresholdSlider?.setValue(threshold);
        this.tauSlider?.setValue(tau);
        this.brightnessSlider?.setValue(brightness);

        const selectedColormap = this.colormapOptions.find((item) => item.value === colormap) ?? this.colormapOptions[0];
        this.setHeatmapColormapDropdownLabel(selectedColormap.label);
        this.updateColormapDropdownActiveItem(selectedColormap.value);
        this.setHeatmapControls({
            blendBase: blendBase,
            negRelevancy: negRelevancy,
            opacity: opacity,
            threshold: threshold,
            tau01: tau,
            brightness01: brightness,
            colormap: colormap,
        })
        this.syncFromSceneState('22222222222222222');
    }

    setupEventHandlers() {
        window.addEventListener('event.panelTabChange', (event: Event) => {
            this.syncFromSceneState('33333333333333333');
        });

        window.addEventListener('event.plyHeatmapLoaded', (event: Event) => {
            const detail = (event as CustomEvent).detail;
            this.onPlyHeatmapLoaded(detail.modelId, detail.modelName, detail.sceneId);
        });

        window.addEventListener('event.plyHeatmapUnloaded', (event: Event) => {
            const detail = (event as CustomEvent).detail;
            this.onPlyHeatmapUnloaded(detail.modelId);
        });
        window.addEventListener('event.sceneRenderModeChange', (event: Event) => {
            this.syncFromSceneState('66666666666666666');
        });
        // window.addEventListener('event.closeMultiView', (event: Event) => {
        //     const detail = (event as CustomEvent).detail;
        //     this.onCloseMultiView(detail.sceneId);
        // });
        window.addEventListener('event.changeMultiView', (event: Event) => {
            const detail = (event as CustomEvent).detail;
            this.onChangeMultiView(detail.msg);
        });
    }

    private onPlyHeatmapLoaded(modelId: string, modelName: string, sceneId: string): void {
        this.plyModelMap.set(sceneId, { modelId, modelName, sceneId, codebook: null, codebookName: null, weightGram64x64: null, weightQuery64: null, loadStatus: 0 });
        this.updateHeatmapPanelModels();
    }

    private onPlyHeatmapUnloaded(modelId: string): void {
        for (const [sceneId, plyModel] of this.plyModelMap.entries()) {
            if (plyModel.modelId === modelId) {
                this.plyModelMap.delete(sceneId);
                break;
            }
        }
        this.updateHeatmapPanelModels();
    }
    // private onCloseMultiView(sceneId: string) {
    //     this.plyModelMap.delete(sceneId);
    //     this.updateHeatmapPanelModels();
    //     this.syncFromSceneState('7777777777');
    // }
    private onChangeMultiView(msg: string) {
        const mainViewToolsAny = globalTools.mainViewTools as any;
        const leftPanelTools = mainViewToolsAny?.sceneViewPanelLeftTools ?? globalTools.mainViewTools?.getLeftSceneViewTools?.();
        const rightPanelTools = mainViewToolsAny?.sceneViewPanelRightTools ?? globalTools.mainViewTools?.getRightSceneViewTools?.();

        const leftScene = leftPanelTools?.mainscene ?? leftPanelTools?.getScene?.();
        const rightScene = rightPanelTools?.mainscene ?? rightPanelTools?.getScene?.();

        const leftSceneId = leftScene?.id ?? leftScene?.getId?.();
        const rightSceneId = rightScene?.id ?? rightScene?.getId?.();

        if (leftSceneId&&!this.plyModelMap.has(leftSceneId)) {
            this.plyModelMap.delete(leftSceneId);
        }
        if (rightSceneId&&!this.plyModelMap.has(rightSceneId)) {
            this.plyModelMap.delete(rightSceneId);
        }

        this.updateHeatmapPanelModels();
        this.syncFromSceneState('99999');
    }
    private updateHeatmapPanelModels() {
        if (!this.plyModelListElement) {
            return;
        }

        const oldDynamicBlocks = this.plyModelListElement.querySelectorAll('.heatmap-model-file-group');
        oldDynamicBlocks.forEach((element) => element.remove());

        const mainViewToolsAny = globalTools.mainViewTools as any;
        const leftPanelTools = mainViewToolsAny?.sceneViewPanelLeftTools ?? globalTools.mainViewTools?.getLeftSceneViewTools?.();
        const rightPanelTools = mainViewToolsAny?.sceneViewPanelRightTools ?? globalTools.mainViewTools?.getRightSceneViewTools?.();

        const leftScene = leftPanelTools?.mainscene ?? leftPanelTools?.getScene?.();
        const rightScene = rightPanelTools?.mainscene ?? rightPanelTools?.getScene?.();

        const leftSceneId = leftScene?.id ?? leftScene?.getId?.();
        const rightSceneId = rightScene?.id ?? rightScene?.getId?.();
        console.log("leftSceneId-------",leftPanelTools,leftSceneId)
        console.log("rightSceneId------",rightPanelTools,rightSceneId)

        const appendModelBlock = (viewLabel: string, viewI18nKey: string, sceneId: string | undefined): Boolean => {
            if (!sceneId) {
                return false;
            }
            const plyModel = this.plyModelMap.get(sceneId);
            if (!plyModel) {
                return false;
            }

            const modelGroup = this.ui.createElement({
                tag: 'div',
                className: 'heatmap-model-file-group',
            });
            const viewTitle = this.ui.createElement({
                tag: 'div',
                textContent: viewLabel,
                className: 'heatmap-sub-title',
                i18nKey: viewI18nKey,
            });

            const codebookRow = this.createFileRow(
                plyModel,
                '.txt,.csv,.bin',
                async (file) => {
                    await this.loadHeatmapCodebookFile(file, sceneId);
                    this.updateHeatmapPanelModels();
                },
                () => {
                    this.clearHeatmapCodebook(sceneId);
                }
            );

            modelGroup.appendChild(viewTitle);
            modelGroup.appendChild(codebookRow);
            this.plyModelListElement?.appendChild(modelGroup);
            return true;
        };

        const hasleft = appendModelBlock('左视图', rightSceneId ? 'heatmap.leftView' : 'heatmap.viewport', leftSceneId);
        const hasright = appendModelBlock('右视图', 'heatmap.rightView', rightSceneId);
        if (this.heatmapModelsEmptyElement && this.heatmapModelsEmptyElement.style) {
            this.heatmapModelsEmptyElement.style.display = (hasleft || hasright) ? 'none' : 'block';
        }

        this.syncFromSceneState('44444444444444444');
    }

    checkViewPanelHasHeatmap(sceneId: string): boolean {
        if (!this.plyModelMap) {
            return false;
        }
        const plyModel = this.plyModelMap.get(sceneId);
        if (!plyModel) {
            return false;
        }
        return plyModel.codebook !== null;
    }

    onClose() {
        globalManager.closeRightToolsBar();
    }

    update() {
        if (!this.isActive) {
            return;
        }
        this.syncFromSceneState('55555555555555555');
    }

    private setPanelInteractivity(isActive: boolean): void {
        isActive = true;
        this.isActive = isActive;
        const panelContainer = this.getChild('panelContainer');
        if (!panelContainer) return;
        let disabled = !isActive;

        if (this.plyModelMap.size === 0) {
            disabled = true;
        } else {
            let hasCodebook = false;
            for (const plyModel of this.plyModelMap.values()) {
                if (plyModel.codebook) {
                    hasCodebook = true;
                    break;
                }
            }
            if (!hasCodebook) {
                disabled = true;
            }
        }

        if (!this.languageQueries || this.languageQueries.length === 0) {
            disabled = true;
        }

        const interactiveElements = panelContainer.querySelectorAll('.heatmap-checkbox-input');
        interactiveElements.forEach((element) => {
            (element as HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement).disabled = disabled;
        });

        this.opacitySlider?.setDisabled(disabled);
        this.thresholdSlider?.setDisabled(disabled);
        this.tauSlider?.setDisabled(disabled);
        this.brightnessSlider?.setDisabled(disabled);
        if (this.queryDropdownButtonElement) {
            this.queryDropdownButtonElement.disabled = disabled;
        }
        if (this.colormapDropdownButtonElement) {
            this.colormapDropdownButtonElement.disabled = disabled;
        }
    }

    private bindParamEvents(): void {
        this.blendBaseCheckbox?.addEventListener('change', () => {
            this.setHeatmapControls({
                blendBase: !!this.blendBaseCheckbox?.checked,
            });
        });

        this.negRelevancyCheckbox?.addEventListener('change', () => {
            this.setHeatmapControls({
                negRelevancy: !!this.negRelevancyCheckbox?.checked,
            });
        });

        this.opacitySlider?.onChange((value) => {
            if (value === null) return;
            this.setHeatmapControls({ opacity: value });
        });

        this.thresholdSlider?.onChange((value) => {
            if (value === null) return;
            this.setHeatmapControls({ threshold: value });
        });

        this.tauSlider?.onChange((value) => {
            if (value === null) return;
            this.setHeatmapControls({ tau01: value });
        });

        this.brightnessSlider?.onChange((value) => {
            if (value === null) return;
            this.setHeatmapControls({ brightness01: value });
        });
    }

    private setColormapOptions(): void {
        if (!this.colormapDropdownButtonElement || !this.colormapDropdownListElement) return;
        const list = this.colormapDropdownListElement;
        const button = this.colormapDropdownButtonElement;
        list.innerHTML = '';
        this.colormapOptions.forEach((item) => {
            const listItem = this.ui.createElement({ tag: 'li' });
            const listLink = this.ui.createElement({
                tag: 'a',
                className: 'dropdown-item vision-dropdown-item',
                textContent: `${item.label}`,
                attributes: {
                    href: '#',
                    'data-value': String(item.value),
                },
            });
            listLink.addEventListener('click', (event) => {
                event.preventDefault();
                this.setHeatmapColormapDropdownLabel(item.label);
                this.updateColormapDropdownActiveItem(item.value);
                this.setHeatmapControls({ colormap: item.value });
            });
            listItem.appendChild(listLink);
            list.appendChild(listItem);
        });
        const selected = this.colormapOptions.some((item) => item.value === this.heatmapColormap) ? this.heatmapColormap : this.colormapOptions[0].value;
        const selectedOption = this.colormapOptions.find((item) => item.value === selected)!;
        this.setHeatmapColormapDropdownLabel(selectedOption.label);
        this.updateColormapDropdownActiveItem(selected);
    }

    private updateColormapDropdownActiveItem(selectedValue: number): void {
        if (!this.colormapDropdownListElement) return;
        const items = this.colormapDropdownListElement.querySelectorAll('.vision-dropdown-item');
        items.forEach((item) => {
            const value = Number(item.getAttribute('data-value'));
            if (value === selectedValue) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });
    }

    private setHeatmapColormapDropdownLabel(text: string): void {
        if (!this.colormapDropdownButtonElement) return;
        const label = this.colormapDropdownButtonElement.querySelector('.heatmap-query-toggle-label');
        if (label) {
            label.textContent = text;
        }
    }
    private setHeatmapQueryDropdownLabel(text: string): void {
        if (!this.queryDropdownButtonElement) return;
        const label = this.queryDropdownButtonElement.querySelector('.heatmap-query-toggle-label');
        if (label) {
            label.textContent = text;
        }
    }

    private setQueryOptions(options: string[]): void {
        if (!this.queryDropdownButtonElement || !this.queryDropdownListElement) return;
        const list = this.queryDropdownListElement;
        list.innerHTML = '';
        if (options.length === 0) {
            this.setHeatmapQueryDropdownLabel('-');
            return;
        }
        options.forEach((item) => {
            // query list过滤掉object,things,stuff,texture，不显示在dropdown列表中，但不能在渲染层过滤，否则颜色渲染会失效
            if (item == 'object' || item == 'things' || item == 'stuff' || item == 'texture') {
                return;
            }

            const listItem = this.ui.createElement({ tag: 'li' });
            const listLink = this.ui.createElement({
                tag: 'a',
                className: 'dropdown-item vision-dropdown-item',
                textContent: item,
                attributes: {
                    href: '#',
                    'data-value': item,
                },
            });
            listLink.addEventListener('click', (event) => {
                event.preventDefault();
                this.setHeatmapQueryDropdownLabel(item);
                this.updateDropdownActiveItem(item);
                this.heatmapQueryName = item;
                globalConfig.heatmapParams.weightQuery64 = null;
                this.updateHeatmapPipeline();
            });
            listItem.appendChild(listLink);
            list.appendChild(listItem);
        });
        const selected = this.heatmapQueryName && options.includes(this.heatmapQueryName) ? this.heatmapQueryName : options[0];
        this.setHeatmapQueryDropdownLabel(selected);
        this.updateDropdownActiveItem(selected);
    }

    private updateDropdownActiveItem(selectedValue: string | null): void {
        if (!this.queryDropdownListElement) return;
        const items = this.queryDropdownListElement.querySelectorAll('.vision-dropdown-item');
        items.forEach((item) => {
            const value = item.getAttribute('data-value');
            if (selectedValue && value === selectedValue) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });
    }

    private syncFromSceneState(tab: string): void {
        // console.log("触发交互同步-----",tab)
        const currentTab = globalManager.getActivePanelTab();
        const currentRenderMode = globalConfig.currentRenderMode;
        if (currentTab === 'heatmap') { // 当前右侧pannel页为heatmap进行状态设置，否则不予处理
            this.setPanelInteractivity(currentRenderMode === globalConfig.renderMode.heatmap)
            if (this.queryListFileContentElement) {
                this.queryListFileContentElement.textContent = this.queryListFileName;
            }
        }
    }

    public setHeatmapControls(values: {
        blendBase?: boolean;
        negRelevancy?: boolean;
        opacity?: number;
        threshold?: number;
        tau01?: number;
        brightness01?: number;
        colormap?: number;
    }): void {
        if (typeof values.blendBase === 'boolean') {
            this.heatmapBlendWithBase = values.blendBase;
            try {
                sceneUtils.setWeightOverlayBlendWithBase(values.blendBase);
            } catch { }
        }
        if (typeof values.negRelevancy === 'boolean') {
            this.heatmapUseNegRelevancy = values.negRelevancy;
        }
        if (typeof values.opacity === 'number') {
            this.heatmapOpacity = values.opacity;
        }
        if (typeof values.threshold === 'number') {
            this.heatmapThreshold = values.threshold;
        }
        if (typeof values.tau01 === 'number') {
            this.heatmapRelevancyTau = values.tau01 * 50.0;
        }
        if (typeof values.brightness01 === 'number') {
            this.heatmapGain = values.brightness01 * 3.0;
            try {
                sceneUtils.setWeightOverlayVisualOptions({ gain: this.heatmapGain });
            } catch { }
        }
        if (typeof values.colormap === 'number') {
            this.heatmapColormap = values.colormap;
            try {
                (sceneUtils as any).setWeightOverlayVisualOptions({
                    colormap: this.heatmapColormap,
                });
            } catch { }
        }
        this.updateHeatmapPipeline();
    }

    public updateHeatmapPipeline(): void {
        if (globalConfig.currentRenderMode !== globalConfig.renderMode.heatmap) {
            sceneUtils.disableHeatmapPipeline();
            return;
        }
        this.updateHeatmapQuery64(true);
        if (!this.isHeatmapReady()) {
            sceneUtils.disableHeatmapPipeline();
            return;
        }
        sceneUtils.updateHeatmapPipeline(this.plyModelMap);
    }

    public getHeatmapControlState(): {
        queryListLoaded: boolean;
        queryName: string | null;
        queryOptions: string[];
        blendBase: boolean;
        negRelevancy: boolean;
        opacity: number;
        threshold: number;
        tau01: number;
        brightness01: number;
        heatmapGain: number;
        relevancyTau: number;
        colormap: number;
    } {
        return {
            queryListLoaded: !!this.languageQueries,
            queryName: this.heatmapQueryName,
            queryOptions: (this.languageQueries || []).map((item) => item.name),
            blendBase: this.heatmapBlendWithBase,
            negRelevancy: this.heatmapUseNegRelevancy,
            opacity: this.heatmapOpacity,
            threshold: this.heatmapThreshold,
            tau01: this.heatmapRelevancyTau / 50.0,
            brightness01: this.heatmapGain / 3.0,
            heatmapGain: this.heatmapGain,
            relevancyTau: this.heatmapRelevancyTau,
            colormap: this.heatmapColormap,
        };
    }

    public getPlyModelBySceneId(sceneId: string): PlyModel | undefined {
        return this.plyModelMap.get(sceneId);
    }

    public getQueryParams(): { languageQueries: Array<{ name: string; vector: number[] }> | null; heatmapQueryName: string | null } | null {
        return {
            languageQueries: this.languageQueries || null,
            heatmapQueryName: this.heatmapQueryName || null,
        };
    }

    private updateHeatmapQuery64(forceRecompute: boolean = false): void {
        if (!this.languageQueries || !this.heatmapQueryName) return;
        const q = this.languageQueries.find(x => x?.name === this.heatmapQueryName);
        if (!q || !Array.isArray(q.vector) || q.vector.length !== 512) return;
        for (const plyModel of this.plyModelMap.values()) {
            if (!forceRecompute && plyModel.weightQuery64) continue;
            if (!plyModel.codebook) continue;
            plyModel.weightQuery64 = computeQueryWeights(plyModel.codebook, new Float32Array(q.vector));
        }
    }

    private isHeatmapReady(): boolean {
        if (globalConfig.currentRenderMode !== globalConfig.renderMode.heatmap) return false;
        if (!this.languageQueries) return false;
        if (!this.heatmapQueryName) return false;
        return true;
    }
}
